import { getOwnValue } from "@frontend-shared/utils";
import {
  File,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  Folder,
  Presentation,
  Video,
} from "lucide-react";

interface IFileIcon {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
  userMessageColor: string;
}

const fileTypeMap: Record<string, IFileIcon> = {
  // NOTE: Google Workspace
  "application/vnd.google-apps.folder": {
    Icon: Folder,
    color: "text-gray-500",
    userMessageColor: "text-gray-200",
  },
  "application/vnd.google-apps.document": {
    Icon: FileText,
    color: "text-blue-500",
    userMessageColor: "text-blue-200",
  },
  "application/vnd.google-apps.spreadsheet": {
    Icon: FileSpreadsheet,
    color: "text-green-500",
    userMessageColor: "text-green-300",
  },
  "application/vnd.google-apps.presentation": {
    Icon: Presentation,
    color: "text-yellow-500",
    userMessageColor: "text-yellow-300",
  },

  // NOTE: Microsoft Office
  "application/mspowerpoint": {
    Icon: Presentation,
    color: "text-yellow-500",
    userMessageColor: "text-yellow-300",
  },
  "application/powerpoint": {
    Icon: Presentation,
    color: "text-yellow-500",
    userMessageColor: "text-yellow-300",
  },
  "application/vnd.ms-powerpoint": {
    Icon: Presentation,
    color: "text-yellow-500",
    userMessageColor: "text-yellow-300",
  },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
    Icon: FileText,
    color: "text-blue-500",
    userMessageColor: "text-blue-200",
  },
  "application/vnd.ms-word": {
    Icon: FileText,
    color: "text-blue-500",
    userMessageColor: "text-blue-200",
  },
  // NOTE: NSAI-2796 — browsers report legacy .doc as "application/msword"
  "application/msword": {
    Icon: FileText,
    color: "text-blue-500",
    userMessageColor: "text-blue-200",
  },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
    Icon: FileSpreadsheet,
    color: "text-green-500",
    userMessageColor: "text-green-300",
  },
  "application/vnd.ms-excel": {
    Icon: FileSpreadsheet,
    color: "text-green-500",
    userMessageColor: "text-green-300",
  },
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": {
    Icon: Presentation,
    color: "text-yellow-500",
    userMessageColor: "text-yellow-300",
  },

  // NOTE: Text/Other
  "application/pdf": {
    Icon: File,
    color: "text-red-600",
    userMessageColor: "text-red-300",
  },
  "application/rtf": {
    Icon: FileText,
    color: "text-gray-600",
    userMessageColor: "text-gray-200",
  },
  "text/plain": {
    Icon: FileText,
    color: "text-gray-600",
    userMessageColor: "text-gray-200",
  },
  "text/csv": {
    Icon: FileSpreadsheet,
    color: "text-green-500",
    userMessageColor: "text-green-300",
  },
  "text/html": {
    Icon: FileCode,
    color: "text-orange-500",
    userMessageColor: "text-orange-300",
  },
};

const DEFAULT_FILE_ICON: IFileIcon = {
  Icon: File,
  color: "text-gray-500",
  userMessageColor: "text-gray-200",
};

export const getFileIcon = (mimeType: string): IFileIcon => {
  if (mimeType.startsWith("image/")) {
    return {
      Icon: FileImage,
      color: "text-purple-500",
      userMessageColor: "text-purple-200",
    };
  }
  if (mimeType.startsWith("video/")) {
    return {
      Icon: Video,
      color: "text-red-500",
      userMessageColor: "text-red-300",
    };
  }

  // NOTE: NSAI-5414 — `mimeType` is the attachment type the uploader's client
  // declared, stored verbatim and shown to other viewers (the admin transcript,
  // a shared chat). A plain `fileTypeMap[mimeType]` resolved "toString",
  // "constructor" or "__proto__" to an inherited `Object.prototype` member:
  // truthy, so the default never applied, and destructuring `Icon` from it gave
  // `undefined`, which React threw on as an element type and unmounted the
  // whole page. Own entries only.
  //
  // NSAI-5492: the own-key lookup is the whole guard. `fileTypeMap` is a
  // module-scoped literal, so an own key always holds a real entry —
  // deliberately no `typeof Icon === "function"` check on top, because lucide
  // icons are `forwardRef` objects and that test would reject every legitimate
  // entry and send the whole map to the default glyph.
  return (
    getOwnValue({ record: fileTypeMap, key: mimeType }) ?? DEFAULT_FILE_ICON
  );
};
